import { useState, useEffect, Fragment } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

// The five roles actually used for estimation.est_user_role
// assignments (confirmed from real usage: Estimator, QuantitySurveyor,
// EstimationManager, BidCommittee, cost_control). Excludes CostSpace-
// only roles (admin, director_operations, finance, site_engineer,
// etc.) that exist in public.roles but have no meaning as a Tender-
// scoped Estimation role.
const ESTIMATION_ROLE_CODES = ['Estimator', 'QuantitySurveyor', 'EstimationManager', 'BidCommittee', 'cost_control']

// Roles that exist only for Estimation (Tender-scoped) and have no
// meaning as a CostSpace Project membership role. cost_control is
// deliberately NOT here -- it's a genuine Shared Role (Model A
// migration) usable at both Project and Tender scope.
const ESTIMATION_ONLY_ROLE_CODES = ['Estimator', 'QuantitySurveyor', 'EstimationManager', 'BidCommittee']

const ROLES = [
  { value:'admin',                label:'Admin' },
  { value:'director_operations',  label:'Director Operations' },
  { value:'director_commercial',  label:'Director Commercial' },
  { value:'pm',                   label:'Project Manager' },
  { value:'cost_control',         label:'Cost Control' },
  { value:'finance',              label:'Finance' },
  { value:'procurement',          label:'Procurement' },
  { value:'site_engineer',        label:'Site Engineer' },
  { value:'viewer',               label:'Viewer' },
]

export default function UsersPage() {
  const { can, profile } = useAuth()
  const [users,  setUsers]  = useState([])
  const [loading,setLoading]= useState(true)
  const [msg,    setMsg]    = useState(null)
  const [invite, setInvite] = useState({ email:'', full_name:'', role:'site_engineer' })
  const [inviting,setInviting]=useState(false)
  // Pending access to predefine for the invitation, applied by
  // activate_invitation_access() only after the invited user actually
  // signs up. Each entry becomes one invitation_access row.
  const [pendingAccess, setPendingAccess] = useState([]) // [{ access_type, project_id?, role_id?, tender_id?, permission? }]
  const [pendingProjectForm, setPendingProjectForm] = useState({ project_id:'', role_id:'' })
  const [pendingEstimationForm, setPendingEstimationForm] = useState({ role_ids:[], scope:'global', tender_id:'' })
  const [pendingPermissionForm, setPendingPermissionForm] = useState({ scope:'project', project_id:'', tender_id:'', permission:'' })
  // One-click Cost Control Engineer onboarding -- calls the existing
  // public.onboard_cost_control_engineer(...) RPC exclusively; this page
  // never performs the individual project_members / invitation_access /
  // est_user_role inserts itself for this flow.
  const [showOnboard, setShowOnboard] = useState(false)
  const [onboardOrgs, setOnboardOrgs] = useState([])
  const [onboardProjects, setOnboardProjects] = useState([])
  const [onboardTenders, setOnboardTenders] = useState([])
  const [onboardForm, setOnboardForm] = useState({ name:'', email:'', organization_id:'', new_org_name:'', project_id:'', tender_id:'' })
  const [onboardOrgMode, setOnboardOrgMode] = useState('existing') // 'existing' | 'new'
  const [onboarding, setOnboarding] = useState(false)
  // Project Membership management state -- project_members.role_id is
  // the authoritative source of truth for a user's role WITHIN a
  // project, entirely separate from the legacy user_profiles.role
  // above (which remains untouched/compatibility-only). memberships is
  // keyed by user_id -> array of { id, project_id, role_id, is_active,
  // projects: {name}, roles: {code} } for rendering; roles/projects are
  // the real dropdown option lists.
  const [expandedUser, setExpandedUser] = useState(null)
  const [expandedTab, setExpandedTab] = useState('memberships') // 'memberships' | 'access' | 'manage'
  const [memberships, setMemberships] = useState({})
  const [projects, setProjects] = useState([])
  const [roles, setRoles] = useState([])
  const [addMembershipForm, setAddMembershipForm] = useState({ project_id: '', role_id: '' })
  const [membershipSaving, setMembershipSaving] = useState(false)

  // --- Additional Permissions section (moved here from Permissions
  // page). Reuses the same user_permissions table and Effective
  // Permissions concept: Inherited (from a role on this scope, via
  // role_permissions) vs Additional (explicit user_permissions grant)
  // vs Not Granted. No new data source or authorization logic. ---
  const [tenders, setTenders] = useState([])
  const [allPermissions, setAllPermissions] = useState([])   // distinct permission codes, from role_permissions
  const [rolePermGrants, setRolePermGrants] = useState({})   // { [role_id]: { [permission]: true } }
  // 'effective' = read-only Effective Access view (module counts,
  // expandable). 'manage' = the Additional Permissions editor. Kept
  // fully separate per the required UX -- reviewing access never
  // implies an edit mode.
  const [expandedAccessModule, setExpandedAccessModule] = useState(null)
  const [accessScopeType, setAccessScopeType] = useState('project') // 'project' | 'tender' -- Effective Access view's own scope picker
  const [accessScopeId, setAccessScopeId] = useState('')
  const [accessUserCostSpaceRoles, setAccessUserCostSpaceRoles] = useState([])
  const [accessUserEstimationRoles, setAccessUserEstimationRoles] = useState([])
  const [accessScopeRoleIds, setAccessScopeRoleIds] = useState([])
  const [additionalGrants, setAdditionalGrants] = useState({})
  const [loadingAccess, setLoadingAccess] = useState(false)
  const [accessSaving, setAccessSaving] = useState(false)
  const [accessSaved, setAccessSaved] = useState(false)
  const [accessError, setAccessError] = useState(null)

  async function loadAccessReferenceData() {
    // Organization-scoped: an admin must only see/select Tenders
    // belonging to their own organization here -- never every Tender
    // in the system. profile.organization_id is the same identity
    // already used elsewhere in this file (see handleInvite below),
    // not a new concept.
    const [{ data: tenderData }, { data: rpData }] = await Promise.all([
      supabase.schema('estimation').from('tender').select('id, name').eq('organization_id', profile.organization_id).order('name'),
      supabase.from('role_permissions').select('role_id, permission, granted'),
    ])
    setTenders(tenderData || [])
    const distinctPerms = [...new Set((rpData || []).map(r => r.permission))].sort()
    setAllPermissions(distinctPerms)
    const map = {}
    ;(rpData || []).forEach(row => {
      if (row.granted) { (map[row.role_id] = map[row.role_id] || {})[row.permission] = true }
    })
    setRolePermGrants(map)
  }

  // Returns the loaded rows directly (not just via setState) so callers
  // that need them immediately -- e.g. computing effective access right
  // after expanding a user -- don't race the async state update.
  async function loadUserAccessSummary(userId) {
    setLoadingAccess(true)
    const roleById = Object.fromEntries(roles.map(r => [r.id, r.code]))
    const [{ data: pmData }, { data: eurData }] = await Promise.all([
      supabase.from('project_members').select('project_id, role_id, projects(name)').eq('user_id', userId).eq('is_active', true),
      supabase.schema('estimation').from('est_user_role').select('role_id, tender_id').eq('user_id', userId).eq('is_active', true),
    ])
    const tenderById = Object.fromEntries(tenders.map(t => [t.id, t.name]))
    const costSpaceRoles = (pmData || []).map(row => ({
      project_id: row.project_id, project_name: row.projects?.name || 'Unknown project',
      role_id: row.role_id, role_code: roleById[row.role_id] || 'Unknown role',
    }))
    const estimationRoles = (eurData || []).map(row => ({
      role_id: row.role_id, role_code: roleById[row.role_id] || 'Unknown role',
      tender_id: row.tender_id, tender_name: row.tender_id ? (tenderById[row.tender_id] || 'Unknown tender') : null,
    }))
    setAccessUserCostSpaceRoles(costSpaceRoles)
    setAccessUserEstimationRoles(estimationRoles)
    setLoadingAccess(false)
    return { costSpaceRoles, estimationRoles }
  }

  async function loadAdditionalGrants(userId, scopeType, scopeId, costSpaceRoles, estimationRoles) {
    setLoadingAccess(true)
    setAccessError(null)
    const roleIds = scopeType === 'project'
      ? costSpaceRoles.filter(r => r.project_id === scopeId).map(r => r.role_id)
      : estimationRoles.filter(r => r.tender_id === scopeId || r.tender_id === null).map(r => r.role_id)
    setAccessScopeRoleIds(roleIds)
    let query = supabase.from('user_permissions').select('permission, granted').eq('user_id', userId)
    query = scopeType === 'project' ? query.eq('project_id', scopeId) : query.eq('tender_id', scopeId)
    const { data, error } = await query
    if (error) { setAccessError(error.message); setLoadingAccess(false); return }
    const map = {}
    ;(data || []).forEach(row => { map[row.permission] = row.granted })
    setAdditionalGrants(map)
    setLoadingAccess(false)
  }

  function toggleAdditionalGrant(permission) {
    setAdditionalGrants(prev => ({ ...prev, [permission]: !prev[permission] }))
  }

  function inheritedFromRole(permission) {
    const roleId = accessScopeRoleIds.find(rid => rolePermGrants[rid]?.[permission])
    if (!roleId) return null
    return roles.find(r => r.id === roleId)?.code || null
  }

  async function saveAdditionalGrants(userId) {
    if (!accessScopeId) return
    setAccessSaving(true)
    setAccessError(null)
    const grantedPermissions = allPermissions.filter(p => additionalGrants[p])
    let delQuery = supabase.from('user_permissions').delete().eq('user_id', userId)
    delQuery = accessScopeType === 'project' ? delQuery.eq('project_id', accessScopeId) : delQuery.eq('tender_id', accessScopeId)
    const { error: delErr } = await delQuery
    if (delErr) { setAccessError(delErr.message); setAccessSaving(false); return }
    if (grantedPermissions.length > 0) {
      const rows = grantedPermissions.map(p => ({
        user_id: userId,
        project_id: accessScopeType === 'project' ? accessScopeId : null,
        tender_id: accessScopeType === 'tender' ? accessScopeId : null,
        permission: p, granted: true,
      }))
      const { error: insErr } = await supabase.from('user_permissions').insert(rows)
      if (insErr) { setAccessError(insErr.message); setAccessSaving(false); return }
    }
    setAccessSaving(false); setAccessSaved(true)
    setTimeout(() => setAccessSaved(false), 2000)
  }

  function handleAccessScopeTypeChange(userId, next) {
    setAccessScopeType(next)
    setAccessScopeId('')
    setAdditionalGrants({})
  }

  function handleAccessScopeIdChange(userId, next, scopeType) {
    setAccessScopeId(next)
    if (next) loadAdditionalGrants(userId, scopeType, next, accessUserCostSpaceRoles, accessUserEstimationRoles)
    else setAdditionalGrants({})
  }

  const accessModuleGroups = allPermissions.reduce((acc, p) => {
    const [m] = p.split('.'); (acc[m] = acc[m] || []).push(p); return acc
  }, {})
  const accessModuleNames = Object.keys(accessModuleGroups).sort()

  // Per-module counts for the compact Effective Access table: Granted =
  // permissions in this module the user has (inherited from a role on
  // the current scope, or explicitly additional); Additional = the
  // subset of those that came specifically from user_permissions.
  // Computed live from grants already loaded above -- never hardcoded.
  const accessModuleCounts = accessModuleNames.map(moduleName => {
    const perms = accessModuleGroups[moduleName]
    let grantedCount = 0, additionalCount = 0
    perms.forEach(p => {
      const inherited = accessScopeRoleIds.some(rid => rolePermGrants[rid]?.[p])
      const additional = !!additionalGrants[p]
      if (inherited || additional) grantedCount++
      if (additional) additionalCount++
    })
    return { moduleName, total: perms.length, granted: grantedCount, additional: additionalCount }
  })

  useEffect(() => { loadUsers(); loadProjectsAndRoles(); loadAccessReferenceData() }, [])

  async function loadProjectsAndRoles() {
    const [{ data: projectsData }, { data: rolesData }] = await Promise.all([
      supabase.from('projects').select('id, name').order('name'),
      supabase.from('roles').select('id, code').order('code'),
    ])
    setProjects(projectsData || [])
    setRoles(rolesData || [])
  }

  async function loadMembershipsForUser(userId) {
    const { data } = await supabase
      .from('project_members')
      .select('id, project_id, role_id, is_active, projects(name), roles(code)')
      .eq('user_id', userId)
      .order('created_at')
    setMemberships(prev => ({ ...prev, [userId]: data || [] }))
  }

  async function toggleExpand(userId) {
    if (expandedUser === userId) { setExpandedUser(null); return }
    setExpandedUser(userId)
    setExpandedTab('memberships')
    setAddMembershipForm({ project_id: '', role_id: '' })
    if (!memberships[userId]) loadMembershipsForUser(userId)
    setAccessScopeType('project'); setAccessScopeId(''); setAdditionalGrants({}); setAccessError(null)
    const { costSpaceRoles, estimationRoles } = await loadUserAccessSummary(userId)
    // Default the Effective Access view to the user's first available
    // scope (their first CostSpace project, else their first
    // Tender-specific Estimation assignment) so it shows something
    // useful immediately instead of an empty state.
    if (costSpaceRoles.length > 0) {
      setAccessScopeType('project'); setAccessScopeId(costSpaceRoles[0].project_id)
      loadAdditionalGrants(userId, 'project', costSpaceRoles[0].project_id, costSpaceRoles, estimationRoles)
    } else {
      const firstTender = estimationRoles.find(r => r.tender_id)
      if (firstTender) {
        setAccessScopeType('tender'); setAccessScopeId(firstTender.tender_id)
        loadAdditionalGrants(userId, 'tender', firstTender.tender_id, costSpaceRoles, estimationRoles)
      }
    }
  }

  async function addMembership(userId) {
    if (!addMembershipForm.project_id || !addMembershipForm.role_id) {
      setMsg({ type:'error', text:'Select both a project and a role.' })
      return
    }
    setMembershipSaving(true)
    // RLS on project_members enforces INSERT is only allowed for
    // is_platform_admin() -- if the signed-in user isn't a platform
    // admin, this fails server-side regardless of what the UI shows.
    const { error } = await supabase.from('project_members').insert({
      project_id: addMembershipForm.project_id,
      user_id: userId,
      role_id: addMembershipForm.role_id,
      is_active: true,
    })
    if (error) {
      setMsg({ type:'error', text:'Failed: ' + error.message })
    } else {
      setMsg({ type:'success', text:'✓ Added to project.' })
      setAddMembershipForm({ project_id: '', role_id: '' })
      loadMembershipsForUser(userId)
    }
    setMembershipSaving(false)
  }

  async function changeMembershipRole(userId, membershipId, newRoleId) {
    const { error } = await supabase.from('project_members').update({ role_id: newRoleId }).eq('id', membershipId)
    if (error) { setMsg({ type:'error', text:'Failed: ' + error.message }); return }
    loadMembershipsForUser(userId)
  }

  async function toggleMembershipActive(userId, membershipId, current) {
    const { error } = await supabase.from('project_members').update({ is_active: !current }).eq('id', membershipId)
    if (error) { setMsg({ type:'error', text:'Failed: ' + error.message }); return }
    loadMembershipsForUser(userId)
  }

  async function loadUsers() {
    const { data } = await supabase.from('user_profiles').select('*').order('created_at')
    setUsers(data || [])
    setLoading(false)
  }

  async function updateRole(id, role) {
    await supabase.from('user_profiles').update({ role }).eq('id', id)
    setUsers(p => p.map(u => u.id === id ? {...u, role} : u))
  }

  async function toggleActive(id, current) {
    await supabase.from('user_profiles').update({ is_active: !current }).eq('id', id)
    setUsers(p => p.map(u => u.id === id ? {...u, is_active: !current} : u))
  }

  function addPendingProject() {
    if (!pendingProjectForm.project_id || !pendingProjectForm.role_id) return
    setPendingAccess(prev => [...prev, { access_type:'project_membership', project_id:pendingProjectForm.project_id, role_id:pendingProjectForm.role_id }])
    setPendingProjectForm({ project_id:'', role_id:'' })
  }

  function addPendingEstimationRole() {
    if (pendingEstimationForm.role_ids.length === 0) return
    if (pendingEstimationForm.scope === 'tender' && !pendingEstimationForm.tender_id) return
    const tenderId = pendingEstimationForm.scope === 'tender' ? pendingEstimationForm.tender_id : null
    setPendingAccess(prev => [
      ...prev,
      ...pendingEstimationForm.role_ids.map(role_id => ({ access_type:'estimation_role', role_id, tender_id: tenderId })),
    ])
    setPendingEstimationForm({ role_ids:[], scope:'global', tender_id:'' })
  }

  function addPendingPermission() {
    if (!pendingPermissionForm.permission) return
    const scopeId = pendingPermissionForm.scope === 'project' ? pendingPermissionForm.project_id : pendingPermissionForm.tender_id
    if (!scopeId) return
    setPendingAccess(prev => [...prev, {
      access_type:'user_permission', permission:pendingPermissionForm.permission,
      project_id: pendingPermissionForm.scope === 'project' ? scopeId : null,
      tender_id: pendingPermissionForm.scope === 'tender' ? scopeId : null,
    }])
    setPendingPermissionForm({ scope:'project', project_id:'', tender_id:'', permission:'' })
  }

  function removePendingAccess(index) {
    setPendingAccess(prev => prev.filter((_, i) => i !== index))
  }

  function describePendingAccess(item) {
    if (item.access_type === 'project_membership') {
      const p = projects.find(x=>x.id===item.project_id)?.name || 'Unknown project'
      const r = roles.find(x=>x.id===item.role_id)?.code || 'Unknown role'
      return `CostSpace: ${r} on ${p}`
    }
    if (item.access_type === 'estimation_role') {
      const r = roles.find(x=>x.id===item.role_id)?.code || 'Unknown role'
      const t = item.tender_id ? (tenders.find(x=>x.id===item.tender_id)?.name || 'Unknown tender') : 'Global (all Tenders)'
      return `Estimation: ${r} — ${t}`
    }
    if (item.access_type === 'user_permission') {
      const scope = item.project_id ? (projects.find(x=>x.id===item.project_id)?.name || 'Unknown project') : (tenders.find(x=>x.id===item.tender_id)?.name || 'Unknown tender')
      return `Additional: ${item.permission} — ${scope}`
    }
    return 'Unknown'
  }

  async function handleInvite(e) {
    e.preventDefault()
    setInviting(true); setMsg(null)
    // Phase 2 (organization-scoped invitations): the invited user's
    // eventual organization_id comes EXCLUSIVELY from the inviting
    // admin's own profile.organization_id -- never inferred from the
    // email domain, existing users, or any client-supplied value. If
    // the inviting admin somehow has no organization_id (should be
    // impossible after Phase 1's NOT NULL backfill), fail closed rather
    // than creating an ambiguous invitation.
    if (!profile?.organization_id) {
      setMsg({ type:'error', text:'Your account has no organization assigned. Cannot create invitation.' })
      setInviting(false); return
    }
    // Check not already in user_profiles or invited_users
    const { data: inProfiles } = await supabase.from('user_profiles').select('id').eq('email', invite.email).single()
    const { data: inInvited }  = await supabase.from('invited_users').select('id').eq('email', invite.email).single()
    if (inProfiles || inInvited) {
      setMsg({ type:'error', text:`${invite.email} already exists.` })
      setInviting(false); return
    }
    const { error } = await supabase.from('invited_users').insert({
      email: invite.email,
      full_name: invite.full_name || invite.email.split('@')[0],
      role: invite.role,
      organization_id: profile.organization_id
    })
    if (error) {
      setMsg({ type:'error', text:'Failed: ' + error.message })
      setInviting(false)
      return
    }
    // Store the predefined access, keyed by email -- applied later by
    // activate_invitation_access() once the invited user actually
    // signs up. Written directly here (not via an RPC) because
    // invitation_access is admin-only by RLS, matching the same
    // authority already used to create the invitation itself.
    if (pendingAccess.length > 0) {
      const rows = pendingAccess.map(item => ({
        email: invite.email,
        access_type: item.access_type,
        project_id: item.project_id ?? null,
        tender_id: item.tender_id ?? null,
        role_id: item.role_id ?? null,
        permission: item.permission ?? null,
      }))
      const { error: accessErr } = await supabase.from('invitation_access').insert(rows)
      if (accessErr) {
        setMsg({ type:'error', text:`Invitation created, but predefined access failed to save: ${accessErr.message}` })
        setInviting(false)
        return
      }
    }
    setMsg({ type:'success', text:`✓ ${invite.email} added. Ask them to Sign Up from the login page with this email.` })
    setInvite({ email:'', full_name:'', role:'site_engineer' })
    setPendingAccess([])
    setInviting(false)
  }

  async function openOnboardModal() {
    setOnboardForm({ name:'', email:'', organization_id:'', new_org_name:'', project_id:'', tender_id:'' })
    setOnboardOrgMode('existing')
    setOnboardProjects([])
    setOnboardTenders([])
    setShowOnboard(true)
    // Projects AND Tenders are intentionally NOT loaded here -- both
    // dropdowns are populated only once an existing Organization is
    // selected (see handleOnboardOrgChange), scoped to that
    // organization_id. This form is Platform-Admin-only (enforced by
    // onboard_cost_control_engineer() itself), so the Organization list
    // legitimately spans every organization -- but Tenders, like
    // Projects, must never be shown unscoped once an organization is
    // actually being chosen from.
    const { data: orgs } = await supabase.from('organizations').select('id, name').order('name')
    setOnboardOrgs(orgs || [])
  }

  // Organization selection changed: clear any previously selected
  // project (it belonged to a different/no organization's project list)
  // and reload the Project dropdown scoped to the newly selected
  // organization_id. Called only from the "existing organization" select.
  async function handleOnboardOrgChange(orgId) {
    setOnboardForm(f => ({ ...f, organization_id: orgId, project_id: '', tender_id: '' }))
    if (!orgId) { setOnboardProjects([]); setOnboardTenders([]); return }
    const [{ data: projects }, { data: tenders }] = await Promise.all([
      supabase.from('projects').select('id, name').eq('organization_id', orgId).order('name'),
      // Tender selector, scoped exactly like Project above -- the fix
      // for the previously unscoped load in openOnboardModal().
      // Platform admin only: the admin-only metadata RPC (id/name/org). A direct
      // SELECT is filtered by RLS to the admin's OWN organization and would hide
      // every customer tender, so it cannot be used here.
      supabase.schema('estimation').rpc('admin_list_customer_tenders'),
    ])
    setOnboardProjects(projects || [])
    setOnboardTenders((tenders || []).filter(t => t.organization_id === orgId).map(t => ({ id: t.tender_id, name: t.tender_name })).sort((x, y) => x.name.localeCompare(y.name)))
  }

  // Switching between "Select Existing" / "Create New" organization mode:
  // a brand-new organization has no project list to scope against, so the
  // Project selection is cleared and disabled until an existing
  // organization is chosen again.
  function handleOnboardOrgModeChange(mode) {
    setOnboardOrgMode(mode)
    setOnboardForm(f => ({ ...f, project_id: '', tender_id: '' }))
    if (mode === 'new') { setOnboardProjects([]); setOnboardTenders([]) }
  }

  async function handleOnboardSubmit(e) {
    e.preventDefault()
    if (onboarding) return
    if (!onboardForm.name.trim() || !onboardForm.email.trim()) {
      setMsg({ type:'error', text:'Engineer name and email are required.' })
      return
    }
    if (onboardOrgMode === 'existing' && !onboardForm.organization_id) {
      setMsg({ type:'error', text:'Select a Company / Organization.' })
      return
    }
    if (onboardOrgMode === 'new' && !onboardForm.new_org_name.trim()) {
      setMsg({ type:'error', text:'Enter a name for the new Company / Organization.' })
      return
    }
    setOnboarding(true); setMsg(null)
    const { data, error } = await supabase.rpc('onboard_cost_control_engineer', {
      p_name: onboardForm.name.trim(),
      p_email: onboardForm.email.trim().toLowerCase(),
      p_organization_id: onboardOrgMode === 'existing' ? onboardForm.organization_id : null,
      p_new_org_name: onboardOrgMode === 'new' ? onboardForm.new_org_name.trim() : null,
      p_project_id: onboardForm.project_id || null,
      p_tender_id: onboardForm.tender_id || null,
    })
    if (error) {
      // Known RPC failure modes surfaced with an admin-friendly message;
      // the raw message is preserved as a fallback for anything else.
      const raw = error.message || ''
      let text = raw
      if (raw.includes('different organization')) text = 'This engineer already belongs to another organization.'
      else if (raw.includes('does not exist') && raw.includes('project')) text = 'The selected CostSpace project is not valid.'
      else if (raw.includes('does not exist') && raw.includes('tender')) text = 'The selected Estimation tender is not valid.'
      else if (raw.includes('platform admin required')) text = 'You do not have permission to onboard engineers.'
      setMsg({ type:'error', text })
      setOnboarding(false)
      return
    }
    setMsg({ type:'success', text: data?.message || 'Engineer access prepared successfully.' })
    setShowOnboard(false)
    setOnboarding(false)
    loadUsers()
  }

  if (!can.manage_users) return (
    <div style={{padding:40,textAlign:'center',color:'#9A948C',fontSize:13}}>Access denied — Admin only</div>
  )

  return (
    <div className="min-h-screen font-sans" style={{background:'#F5F1EA'}}>
      <div style={{background:'#F5F1EA',borderBottom:'1px solid #DDD9D0',padding:'20px 32px'}}>
        <div className="max-w-4xl mx-auto" style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end'}}>
          <div>
            <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.14em',color:'#9A948C',marginBottom:4}}>Admin</p>
            <h1 style={{fontSize:22,fontWeight:800,color:'#1C1A18'}}>Users & Roles</h1>
          </div>
          <button onClick={openOnboardModal}
            style={{fontSize:13,fontWeight:700,padding:'10px 18px',background:'#1A7A4A',color:'#fff',border:'none',borderRadius:6,cursor:'pointer'}}>
            + Quick Add User
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto" style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:16}}>

        {msg && (
          <div style={{padding:'10px 16px',borderRadius:8,background:msg.type==='success'?'#f0fdf4':msg.type==='info'?'#eff6ff':'#fef2f2',border:`1px solid ${msg.type==='success'?'#bbf7d0':msg.type==='info'?'#bfdbfe':'#fecaca'}`,fontSize:12,color:msg.type==='success'?'#15803d':msg.type==='info'?'#1d4ed8':'#991b1b',display:'flex',justifyContent:'space-between'}}>
            <span>{msg.text}</span>
            <button onClick={()=>setMsg(null)} style={{background:'none',border:'none',cursor:'pointer',color:'inherit'}}>✕</button>
          </div>
        )}

        {/* Invite */}
        <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,padding:'20px 22px'}}>
          <p style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#1C1A18',marginBottom:14}}>Invite New User</p>
          <form onSubmit={handleInvite} style={{display:'grid',gridTemplateColumns:'1fr 1fr auto auto',gap:10,alignItems:'end'}}>
            <div>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Email</p>
              <input type="email" required value={invite.email} onChange={e=>setInvite(p=>({...p,email:e.target.value}))}
                placeholder="user@email.com"
                style={{width:'100%',padding:'8px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,outline:'none',color:'#1C1A18'}}/>
            </div>
            <div>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Full Name</p>
              <input type="text" value={invite.full_name} onChange={e=>setInvite(p=>({...p,full_name:e.target.value}))}
                placeholder="Full name"
                style={{width:'100%',padding:'8px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,outline:'none',color:'#1C1A18'}}/>
            </div>
            <div>
              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Role</p>
              <select value={invite.role} onChange={e=>setInvite(p=>({...p,role:e.target.value}))}
                style={{padding:'8px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,outline:'none',color:'#1C1A18',background:'#fff'}}>
                {ROLES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <button type="submit" disabled={inviting}
              style={{padding:'8px 16px',background:'#1A7A4A',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
              {inviting?'…':'+ Invite'}
            </button>
          </form>

          {/* Predefine Access -- applied only after the invited user
              actually signs up, via the secure activation RPC. Never
              writes directly to project_members/est_user_role/
              user_permissions from here. */}
          <div style={{marginTop:16,paddingTop:14,borderTop:'1px solid #DDD9D0'}}>
            <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:8}}>Predefine Access (optional — applied on Sign Up)</p>

            <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:10}}>
              <div style={{display:'flex',gap:6,alignItems:'end'}}>
                <div>
                  <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>CostSpace Project</p>
                  <select value={pendingProjectForm.project_id} onChange={e=>setPendingProjectForm(f=>({...f,project_id:e.target.value}))}
                    style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:140}}>
                    <option value="">— Select —</option>
                    {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Role</p>
                  <select value={pendingProjectForm.role_id} onChange={e=>setPendingProjectForm(f=>({...f,role_id:e.target.value}))}
                    style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:120}}>
                    <option value="">— Select —</option>
                    {roles.filter(r=>!ESTIMATION_ONLY_ROLE_CODES.includes(r.code)).map(r=><option key={r.id} value={r.id}>{r.code}</option>)}
                  </select>
                </div>
                <button type="button" onClick={addPendingProject} style={{fontSize:11,padding:'5px 10px',border:'1px solid #DDD9D0',borderRadius:5,background:'#fff',color:'#1A7A4A',cursor:'pointer',fontWeight:600}}>+ Add</button>
              </div>

              <div style={{display:'flex',gap:6,alignItems:'end'}}>
                <div>
                  <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Estimation Role(s)</p>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,background:'#fff',minWidth:280}}>
                    {roles.filter(r=>ESTIMATION_ROLE_CODES.includes(r.code)).map(r=>{
                      const checked = pendingEstimationForm.role_ids.includes(r.id)
                      return (
                        <label key={r.id} style={{display:'flex',alignItems:'center',gap:4,fontSize:11,cursor:'pointer'}}>
                          <input type="checkbox" checked={checked} onChange={()=>setPendingEstimationForm(f=>({
                            ...f,
                            role_ids: checked ? f.role_ids.filter(id=>id!==r.id) : [...f.role_ids, r.id],
                          }))} />
                          {r.code}
                        </label>
                      )
                    })}
                  </div>
                </div>
                <div>
                  <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Scope</p>
                  <select value={pendingEstimationForm.scope} onChange={e=>setPendingEstimationForm(f=>({...f,scope:e.target.value}))}
                    style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:100}}>
                    <option value="global">Global</option>
                    <option value="tender">Tender</option>
                  </select>
                </div>
                {pendingEstimationForm.scope === 'tender' && (
                  <div>
                    <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Tender</p>
                    <select value={pendingEstimationForm.tender_id} onChange={e=>setPendingEstimationForm(f=>({...f,tender_id:e.target.value}))}
                      style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:140}}>
                      <option value="">— Select —</option>
                      {tenders.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                <button type="button" onClick={addPendingEstimationRole} style={{fontSize:11,padding:'5px 10px',border:'1px solid #DDD9D0',borderRadius:5,background:'#fff',color:'#1A7A4A',cursor:'pointer',fontWeight:600}}>+ Add</button>
              </div>

              <div style={{display:'flex',gap:6,alignItems:'end'}}>
                <div>
                  <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Additional Permission</p>
                  <select value={pendingPermissionForm.permission} onChange={e=>setPendingPermissionForm(f=>({...f,permission:e.target.value}))}
                    style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:160}}>
                    <option value="">— Select —</option>
                    {allPermissions.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Scope</p>
                  <select value={pendingPermissionForm.scope} onChange={e=>setPendingPermissionForm(f=>({...f,scope:e.target.value}))}
                    style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:100}}>
                    <option value="project">Project</option>
                    <option value="tender">Tender</option>
                  </select>
                </div>
                {pendingPermissionForm.scope === 'project' ? (
                  <select value={pendingPermissionForm.project_id} onChange={e=>setPendingPermissionForm(f=>({...f,project_id:e.target.value}))}
                    style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:140}}>
                    <option value="">— Select —</option>
                    {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                ) : (
                  <select value={pendingPermissionForm.tender_id} onChange={e=>setPendingPermissionForm(f=>({...f,tender_id:e.target.value}))}
                    style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:140}}>
                    <option value="">— Select —</option>
                    {tenders.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                <button type="button" onClick={addPendingPermission} style={{fontSize:11,padding:'5px 10px',border:'1px solid #DDD9D0',borderRadius:5,background:'#fff',color:'#1A7A4A',cursor:'pointer',fontWeight:600}}>+ Add</button>
              </div>
            </div>

            {pendingAccess.length > 0 && (
              <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:6,padding:'8px 10px'}}>
                {pendingAccess.map((item, i) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:11,padding:'3px 0'}}>
                    <span>{describePendingAccess(item)}</span>
                    <button type="button" onClick={()=>removePendingAccess(i)} style={{fontSize:10,color:'#A32D2D',background:'none',border:'none',cursor:'pointer'}}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Users Table */}
        <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#F5F1EA',borderBottom:'1px solid #DDD9D0'}}>
                {['Name','Email','Role','Status','Projects',''].map(h=>(
                  <th key={h} style={{padding:'8px 14px',textAlign:'left',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u=>(
                <>
                <tr key={u.id} style={{borderBottom:'1px solid #F5F1EA'}}>
                  <td style={{padding:'10px 14px',fontWeight:600,color:'#1C1A18'}}>{u.full_name||'—'}</td>
                  <td style={{padding:'10px 14px',color:'#6B655C'}}>{u.email}</td>
                  <td style={{padding:'10px 14px'}}>
                    <select value={u.role} onChange={e=>updateRole(u.id,e.target.value)}
                      style={{padding:'4px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',color:'#1C1A18',background:'#fff'}}>
                      {ROLES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </td>
                  <td style={{padding:'10px 14px'}}>
                    <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,background:u.is_active?'#f0fdf4':'#fef2f2',color:u.is_active?'#1A7A4A':'#A32D2D',border:`1px solid ${u.is_active?'#bbf7d0':'#fecaca'}`}}>
                      {u.is_active?'Active':'Inactive'}
                    </span>
                  </td>
                  <td style={{padding:'10px 14px'}}>
                    <button onClick={()=>toggleExpand(u.id)}
                      style={{fontSize:11,padding:'3px 10px',border:'1px solid #DDD9D0',borderRadius:5,background:expandedUser===u.id?'#EFF6FF':'#fff',color:'#1D4ED8',cursor:'pointer'}}>
                      {expandedUser===u.id ? 'Hide' : 'Manage'} ({(memberships[u.id]||[]).filter(m=>m.is_active).length})
                    </button>
                  </td>
                  <td style={{padding:'10px 14px'}}>
                    <button onClick={()=>toggleActive(u.id,u.is_active)}
                      style={{fontSize:11,padding:'3px 10px',border:'1px solid #DDD9D0',borderRadius:5,background:'#fff',color:'#6B655C',cursor:'pointer'}}>
                      {u.is_active?'Deactivate':'Activate'}
                    </button>
                  </td>
                </tr>
                {expandedUser === u.id && (
                  <tr>
                    <td colSpan={6} style={{padding:'14px 20px',background:'#FAFAF8',borderBottom:'1px solid #DDD9D0'}}>
                      <div style={{display:'flex',gap:4,marginBottom:14,borderBottom:'1px solid #DDD9D0'}}>
                        {[
                          {key:'memberships', label:'Project Memberships'},
                          {key:'access', label:'Access Overview'},
                          {key:'manage', label:'Manage Permissions'},
                        ].map(tab => (
                          <button key={tab.key} onClick={()=>setExpandedTab(tab.key)}
                            style={{fontSize:11,fontWeight:700,padding:'7px 14px',border:'none',borderBottom:expandedTab===tab.key?'2px solid #1A7A4A':'2px solid transparent',background:'none',color:expandedTab===tab.key?'#1A7A4A':'#9A948C',cursor:'pointer'}}>
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      {expandedTab === 'memberships' && (
                      <>
                      <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:8}}>Project Memberships</p>
                      {(memberships[u.id]||[]).length === 0 ? (
                        <p style={{fontSize:11,color:'#9A948C',marginBottom:10}}>No project memberships yet.</p>
                      ) : (
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11,marginBottom:10}}>
                          <thead>
                            <tr style={{borderBottom:'1px solid #DDD9D0'}}>
                              {['Project','Role','Status',''].map(h=>(
                                <th key={h} style={{padding:'4px 8px',textAlign:'left',fontSize:9,fontWeight:700,color:'#9A948C',textTransform:'uppercase'}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(memberships[u.id]||[]).map(m=>(
                              <tr key={m.id} style={{borderBottom:'1px solid #F0EEE8'}}>
                                <td style={{padding:'6px 8px',color:'#1C1A18'}}>{m.projects?.name || '—'}</td>
                                <td style={{padding:'6px 8px'}}>
                                  <select value={m.role_id} onChange={e=>changeMembershipRole(u.id, m.id, e.target.value)}
                                    style={{padding:'3px 6px',border:'1px solid #DDD9D0',borderRadius:4,fontSize:11,outline:'none',background:'#fff'}}>
                                    {roles.map(r=><option key={r.id} value={r.id}>{r.code}</option>)}
                                  </select>
                                </td>
                                <td style={{padding:'6px 8px'}}>
                                  <span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:20,background:m.is_active?'#f0fdf4':'#fef2f2',color:m.is_active?'#1A7A4A':'#A32D2D'}}>
                                    {m.is_active?'Active':'Inactive'}
                                  </span>
                                </td>
                                <td style={{padding:'6px 8px'}}>
                                  <button onClick={()=>toggleMembershipActive(u.id, m.id, m.is_active)}
                                    style={{fontSize:10,padding:'2px 8px',border:'1px solid #DDD9D0',borderRadius:4,background:'#fff',color:'#6B655C',cursor:'pointer'}}>
                                    {m.is_active?'Deactivate':'Activate'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <div style={{display:'flex',gap:8,alignItems:'end'}}>
                        <div>
                          <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Project</p>
                          <select value={addMembershipForm.project_id} onChange={e=>setAddMembershipForm(f=>({...f,project_id:e.target.value}))}
                            style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:160}}>
                            <option value="">— Select —</option>
                            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Role</p>
                          <select value={addMembershipForm.role_id} onChange={e=>setAddMembershipForm(f=>({...f,role_id:e.target.value}))}
                            style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:140}}>
                            <option value="">— Select —</option>
                            {roles.map(r=><option key={r.id} value={r.id}>{r.code}</option>)}
                          </select>
                        </div>
                        <button onClick={()=>addMembership(u.id)} disabled={membershipSaving}
                          style={{padding:'6px 14px',background:'#1A7A4A',color:'#fff',border:'none',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          {membershipSaving ? '…' : '+ Add to Project'}
                        </button>
                      </div>

                      </>
                      )}

                      {expandedTab === 'access' && (
                      <>
                        {loadingAccess ? (
                          <p style={{fontSize:11,color:'#9A948C'}}>Loading…</p>
                        ) : (
                          <>
                            {/* Compact Summary — always visible, no
                                scope selection needed. Answers "what
                                roles does this user have" at a glance. */}
                            <div style={{display:'flex',flexWrap:'wrap',gap:'6px 18px',marginBottom:14,fontSize:11}}>
                              {accessUserCostSpaceRoles.length === 0 && accessUserEstimationRoles.length === 0 ? (
                                <span style={{color:'#9A948C'}}>No CostSpace or Estimation access.</span>
                              ) : (
                                <>
                                  {Object.entries(accessUserCostSpaceRoles.reduce((acc, r) => {
                                    (acc[r.role_code] = acc[r.role_code] || []).push(r.project_name); return acc
                                  }, {})).map(([roleCode, projectNames]) => (
                                    <span key={'cs-'+roleCode}>
                                      <strong>{roleCode}</strong> — {projectNames.length} Project{projectNames.length===1?'':'s'}
                                    </span>
                                  ))}
                                  {Object.entries(accessUserEstimationRoles.reduce((acc, r) => {
                                    (acc[r.role_code] = acc[r.role_code] || []).push(r); return acc
                                  }, {})).map(([roleCode, rows]) => {
                                    const globalCount = rows.filter(r => r.tender_id === null).length
                                    const tenderCount = rows.filter(r => r.tender_id !== null).length
                                    const parts = []
                                    if (globalCount > 0) parts.push('Global')
                                    if (tenderCount > 0) parts.push(`${tenderCount} Tender${tenderCount===1?'':'s'}`)
                                    return (
                                      <span key={'est-'+roleCode}>
                                        <strong>{roleCode}</strong> — {parts.join(' + ')}
                                      </span>
                                    )
                                  })}
                                </>
                              )}
                            </div>

                            {/* Effective Access — read-only, organized
                                by module with counts. No edit affordance
                                here; expanding a module shows per-
                                permission source (Inherited from X /
                                Additional / Not granted). */}
                            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                              <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',margin:0}}>Effective Access</p>
                              <select value={accessScopeType==='project'?accessScopeId:''} onChange={e=>{ if(e.target.value){ setAccessScopeType('project'); handleAccessScopeIdChange(u.id, e.target.value, 'project') } }}
                                style={{padding:'3px 6px',border:'1px solid #DDD9D0',borderRadius:4,fontSize:10,outline:'none',background:'#fff'}}>
                                <option value="">— Project —</option>
                                {accessUserCostSpaceRoles.map(r=>(
                                  <option key={r.project_id} value={r.project_id}>{r.project_name}</option>
                                ))}
                              </select>
                              <select value={accessScopeType==='tender'?accessScopeId:''} onChange={e=>{ if(e.target.value){ setAccessScopeType('tender'); handleAccessScopeIdChange(u.id, e.target.value, 'tender') } }}
                                style={{padding:'3px 6px',border:'1px solid #DDD9D0',borderRadius:4,fontSize:10,outline:'none',background:'#fff'}}>
                                <option value="">— Tender —</option>
                                {accessUserEstimationRoles.filter(r=>r.tender_id).map(r=>(
                                  <option key={r.tender_id} value={r.tender_id}>{r.tender_name}</option>
                                ))}
                              </select>
                            </div>

                            {accessScopeId && (
                              <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:8,overflow:'hidden',maxWidth:520,marginBottom:14}}>
                                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                                  <thead>
                                    <tr style={{borderBottom:'1px solid #DDD9D0',background:'#FAFAF8'}}>
                                      {['Module','Granted','Additional',''].map(h=>(
                                        <th key={h} style={{padding:'5px 10px',textAlign:h==='Module'?'left':'center',fontSize:9,fontWeight:700,color:'#9A948C',textTransform:'uppercase'}}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {accessModuleCounts.filter(m => m.granted > 0).map(m => (
                                      <Fragment key={m.moduleName}>
                                        <tr style={{borderBottom:'1px solid #F0EEE8'}}>
                                          <td style={{padding:'5px 10px',color:'#1C1A18'}}>{m.moduleName}</td>
                                          <td style={{padding:'5px 10px',textAlign:'center'}}>{m.granted}/{m.total}</td>
                                          <td style={{padding:'5px 10px',textAlign:'center'}}>{m.additional || '—'}</td>
                                          <td style={{padding:'5px 10px',textAlign:'center'}}>
                                            <button onClick={()=>setExpandedAccessModule(x=>x===m.moduleName?null:m.moduleName)}
                                              style={{fontSize:10,color:'#1A7A4A',background:'none',border:'none',cursor:'pointer',padding:0}}>
                                              {expandedAccessModule===m.moduleName?'Hide':'View'}
                                            </button>
                                          </td>
                                        </tr>
                                        {expandedAccessModule===m.moduleName && (
                                          <tr>
                                            <td colSpan={4} style={{padding:'6px 10px 10px 20px',background:'#FAFAF8'}}>
                                              {accessModuleGroups[m.moduleName].map(permission => {
                                                const inheritedRole = inheritedFromRole(permission)
                                                const isAdditional = !!additionalGrants[permission]
                                                const label = inheritedRole ? `Inherited from ${inheritedRole}` : isAdditional ? 'Additional' : 'Not granted'
                                                const color = inheritedRole ? '#1A7A4A' : isAdditional ? '#1A7A4A' : '#C9C4BA'
                                                return (
                                                  <div key={permission} style={{display:'flex',justifyContent:'space-between',padding:'3px 0',fontSize:10.5}}>
                                                    <span>{permission}</span>
                                                    <span style={{color,fontWeight:inheritedRole||isAdditional?700:400}}>{label}</span>
                                                  </div>
                                                )
                                              })}
                                            </td>
                                          </tr>
                                        )}
                                      </Fragment>
                                    ))}
                                    {accessModuleCounts.every(m => m.granted === 0) && (
                                      <tr><td colSpan={4} style={{padding:'10px',textAlign:'center',color:'#9A948C'}}>No permissions granted on this scope.</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </>
                      )}

                      {expandedTab === 'manage' && (
                              <div style={{padding:'12px 14px',background:'#FAFAF8',border:'1px solid #DDD9D0',borderRadius:8}}>
                                <div style={{display:'flex',gap:8,alignItems:'end',marginBottom:10}}>
                                  <div>
                                    <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Scope</p>
                                    <select value={accessScopeType} onChange={e=>handleAccessScopeTypeChange(u.id, e.target.value)}
                                      style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:140}}>
                                      <option value="project">CostSpace Project</option>
                                      <option value="tender">Estimation Tender</option>
                                    </select>
                                  </div>
                                  {accessScopeType === 'project' ? (
                                    <div>
                                      <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Project</p>
                                      <select value={accessScopeId} onChange={e=>handleAccessScopeIdChange(u.id, e.target.value, 'project')}
                                        style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:160}}>
                                        <option value="">— Select —</option>
                                        {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                                      </select>
                                    </div>
                                  ) : (
                                    <div>
                                      <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',color:'#9A948C',marginBottom:3}}>Tender</p>
                                      <select value={accessScopeId} onChange={e=>handleAccessScopeIdChange(u.id, e.target.value, 'tender')}
                                        style={{padding:'5px 8px',border:'1px solid #DDD9D0',borderRadius:5,fontSize:11,outline:'none',background:'#fff',minWidth:160}}>
                                        <option value="">— Select —</option>
                                        {tenders.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                                      </select>
                                    </div>
                                  )}
                                  {accessScopeId && (
                                    <button onClick={()=>saveAdditionalGrants(u.id)} disabled={accessSaving}
                                      style={{padding:'6px 14px',background:accessSaved?'#15803d':'#1A7A4A',color:'#fff',border:'none',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                      {accessSaving ? '…' : accessSaved ? '✓ Saved' : 'Save Additional Permissions'}
                                    </button>
                                  )}
                                </div>

                                {accessError && (
                                  <div style={{padding:'8px 12px',borderRadius:6,background:'#fef2f2',border:'1px solid #fecaca',fontSize:11,color:'#991b1b',marginBottom:10}}>
                                    {accessError}
                                  </div>
                                )}

                                {accessScopeId && (
                                  loadingAccess ? (
                                    <p style={{fontSize:11,color:'#9A948C'}}>Loading…</p>
                                  ) : (
                                    <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:8,overflow:'hidden',maxWidth:700,maxHeight:360,overflowY:'auto'}}>
                                      <p style={{fontSize:10,color:'#9A948C',padding:'6px 12px',margin:0,borderBottom:'1px solid #F0EEE8'}}>
                                        Effective permissions on this {accessScopeType === 'project' ? 'Project' : 'Tender'}. Inherited permissions come from the user's role(s) and can't be removed here.
                                      </p>
                                      {accessModuleNames.map(moduleName => (
                                        <div key={moduleName}>
                                          <div style={{background:'#FAFAF8',padding:'5px 12px',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#6B655C'}}>{moduleName}</div>
                                          {accessModuleGroups[moduleName].map(permission => {
                                            const inheritedRole = inheritedFromRole(permission)
                                            const isAdditional = !!additionalGrants[permission]
                                            if (inheritedRole) {
                                              return (
                                                <div key={permission} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 12px',fontSize:11,borderBottom:'1px solid #F0EEE8'}}>
                                                  <span style={{width:14,height:14,borderRadius:3,background:'#1A7A4A',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
                                                  </span>
                                                  <span>{permission}</span>
                                                  <span style={{fontSize:9,color:'#9A948C',marginLeft:'auto'}}>Inherited from <strong>{inheritedRole}</strong></span>
                                                </div>
                                              )
                                            }
                                            return (
                                              <label key={permission} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 12px',fontSize:11,cursor:'pointer',borderBottom:'1px solid #F0EEE8'}}>
                                                <input type="checkbox" checked={isAdditional} onChange={() => toggleAdditionalGrant(permission)} />
                                                <span>{permission}</span>
                                                <span style={{fontSize:9,color:isAdditional?'#1A7A4A':'#C9C4BA',marginLeft:'auto',fontWeight:isAdditional?700:400}}>
                                                  {isAdditional ? 'Additional' : 'Not granted'}
                                                </span>
                                              </label>
                                            )
                                          })}
                                        </div>
                                      ))}
                                    </div>
                                  )
                                )}
                              </div>
                      )}
                    </td>
                  </tr>
                )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showOnboard && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => !onboarding && setShowOnboard(false)}>
          <form onSubmit={handleOnboardSubmit} onClick={(e) => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:10, padding:28, width:440, maxHeight:'90vh', overflowY:'auto', display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <h2 style={{fontSize:17,fontWeight:800,color:'#1C1A18',margin:0}}>Quick Add User</h2>
              <p style={{fontSize:12,color:'#9A948C',marginTop:4}}>One-click onboarding — the engineer receives a standard operational access profile automatically.</p>
            </div>

            <div>
              <label style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#9A948C',display:'block',marginBottom:6}}>Engineer Name *</label>
              <input value={onboardForm.name} onChange={e=>setOnboardForm(f=>({...f,name:e.target.value}))} required
                style={{width:'100%',padding:'10px 12px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:13,boxSizing:'border-box'}} />
            </div>

            <div>
              <label style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#9A948C',display:'block',marginBottom:6}}>Engineer Email *</label>
              <input type="email" value={onboardForm.email} onChange={e=>setOnboardForm(f=>({...f,email:e.target.value}))} required
                style={{width:'100%',padding:'10px 12px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:13,boxSizing:'border-box'}} />
            </div>

            <div>
              <label style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#9A948C',display:'block',marginBottom:6}}>Company / Organization *</label>
              <div style={{display:'flex',gap:6,marginBottom:8}}>
                <button type="button" onClick={()=>handleOnboardOrgModeChange('existing')}
                  style={{flex:1,padding:8,fontSize:12,fontWeight:600,borderRadius:6,border:'1px solid #DDD9D0',cursor:'pointer',
                    background:onboardOrgMode==='existing'?'#1C1A18':'#fff',color:onboardOrgMode==='existing'?'#fff':'#1C1A18'}}>
                  Select Existing
                </button>
                <button type="button" onClick={()=>handleOnboardOrgModeChange('new')}
                  style={{flex:1,padding:8,fontSize:12,fontWeight:600,borderRadius:6,border:'1px solid #DDD9D0',cursor:'pointer',
                    background:onboardOrgMode==='new'?'#1C1A18':'#fff',color:onboardOrgMode==='new'?'#fff':'#1C1A18'}}>
                  Create New
                </button>
              </div>
              {onboardOrgMode === 'existing' ? (
                <select value={onboardForm.organization_id} onChange={e=>handleOnboardOrgChange(e.target.value)}
                  style={{width:'100%',padding:'10px 12px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:13}}>
                  <option value="">— Select organization —</option>
                  {onboardOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              ) : (
                <input value={onboardForm.new_org_name} onChange={e=>setOnboardForm(f=>({...f,new_org_name:e.target.value}))}
                  placeholder="New company name"
                  style={{width:'100%',padding:'10px 12px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:13,boxSizing:'border-box'}} />
              )}
            </div>

            <div>
              <label style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#9A948C',display:'block',marginBottom:6}}>CostSpace Project (optional)</label>
              <select value={onboardForm.project_id} onChange={e=>setOnboardForm(f=>({...f,project_id:e.target.value}))}
                disabled={onboardOrgMode==='new' || !onboardForm.organization_id}
                style={{width:'100%',padding:'10px 12px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:13,
                  background:(onboardOrgMode==='new' || !onboardForm.organization_id)?'#F5F1EA':'#fff',
                  cursor:(onboardOrgMode==='new' || !onboardForm.organization_id)?'not-allowed':'auto'}}>
                <option value="">— None —</option>
                {onboardProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {onboardOrgMode==='new' && (
                <p style={{fontSize:11,color:'#9A948C',marginTop:4}}>Project selection is unavailable while creating a new organization.</p>
              )}
              {onboardOrgMode==='existing' && onboardForm.organization_id && onboardProjects.length===0 && (
                <p style={{fontSize:11,color:'#9A948C',marginTop:4}}>This organization has no CostSpace projects yet.</p>
              )}
            </div>

            <div>
              <label style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#9A948C',display:'block',marginBottom:6}}>Estimation Tender (optional)</label>
              <select value={onboardForm.tender_id} onChange={e=>setOnboardForm(f=>({...f,tender_id:e.target.value}))}
                style={{width:'100%',padding:'10px 12px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:13}}>
                <option value="">— None —</option>
                {onboardTenders.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            <div style={{background:'#F5F1EA',border:'1px solid #DDD9D0',borderRadius:6,padding:'10px 12px'}}>
              <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',color:'#9A948C',marginBottom:2}}>Access Profile</div>
              <div style={{fontSize:13,fontWeight:600,color:'#1C1A18'}}>Full Cost Control Engineer</div>
            </div>

            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
              <button type="button" onClick={()=>setShowOnboard(false)} disabled={onboarding}
                style={{padding:'10px 16px',fontSize:13,fontWeight:600,borderRadius:6,border:'1px solid #DDD9D0',background:'#fff',color:'#1C1A18',cursor:'pointer'}}>
                Cancel
              </button>
              <button type="submit" disabled={onboarding}
                style={{padding:'10px 16px',fontSize:13,fontWeight:700,borderRadius:6,border:'none',background:'#1A7A4A',color:'#fff',cursor:onboarding?'wait':'pointer'}}>
                {onboarding ? 'Creating…' : 'Create & Invite'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
